import { IonIcon, IonSpinner } from '@ionic/react';
import {
  arrowForwardOutline,
  checkmarkCircleOutline,
  helpCircleOutline,
  refreshOutline,
  trashBinOutline,
} from 'ionicons/icons';
import React from 'react';
import { DuplicatePlan } from '../useDuplicatePlan';

type Props = {
  plan: DuplicatePlan;
  busy: boolean;
};

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`;

/**
 * The first thing a user sees: what we found, what we already know how to fix,
 * and what still needs them. Everything else on the page is downstream of this.
 */
const PlanOverview: React.FC<Props> = ({ plan, busy }) => {
  const { totals } = plan;
  const hasClear = totals.clearSets > 0;
  const hasReview = totals.reviewSets > 0;

  return (
    <div className="dup-stage dup-overview">
      <header className="dup-overview-head">
        <p className="dup-eyebrow">Duplicate cleanup</p>
        <h1>
          You have <span className="dup-figure">{totals.sets}</span> sets of
          duplicates.
        </h1>
        <p className="dup-lede">
          That&apos;s <strong>{plural(totals.extraFiles, 'extra file')}</strong>{' '}
          taking up room. Each set keeps one copy — you decide which.
        </p>
        {plan.crateIndexLoading && (
          <p className="dup-crate-loading">Reading your Serato crates…</p>
        )}
      </header>

      <div className="dup-choices">
        {hasClear && (
          <section className="dup-choice dup-choice--primary">
            <div className="dup-choice-icon">
              <IonIcon icon={checkmarkCircleOutline} />
            </div>
            <h2>
              <span className="dup-figure">{totals.clearSets}</span> are
              clear-cut
            </h2>
            <p>
              One copy is plainly the best in these — it&apos;s the one with
              your cues, or it&apos;s higher quality, or the others are named
              like copies. Removing{' '}
              <strong>{plural(totals.clearFiles, 'file')}</strong>.
            </p>
            <button
              type="button"
              className="dup-btn dup-btn--primary dup-btn--block"
              onClick={plan.applyClearSets}
              disabled={busy}
            >
              {busy ? (
                <IonSpinner name="dots" />
              ) : (
                <>
                  <IonIcon icon={trashBinOutline} />
                  Clean up these {totals.clearSets}
                </>
              )}
            </button>
          </section>
        )}

        {hasReview && (
          <section className="dup-choice">
            <div className="dup-choice-icon dup-choice-icon--ask">
              <IonIcon icon={helpCircleOutline} />
            </div>
            <h2>
              <span className="dup-figure">{totals.reviewSets}</span> need you
            </h2>
            <p>
              I&apos;m not confident about these. They differ in length, you
              have cues on more than one copy, or the copies look equally good.
              I&apos;ll show them one at a time.
            </p>
            <button
              type="button"
              className="dup-btn dup-btn--ghost dup-btn--block"
              onClick={() => plan.setStage('review')}
              disabled={busy}
            >
              Review them one by one
              <IonIcon icon={arrowForwardOutline} />
            </button>
          </section>
        )}

        {!hasClear && !hasReview && (
          <section className="dup-choice dup-choice--empty">
            <div className="dup-choice-icon">
              <IonIcon icon={checkmarkCircleOutline} />
            </div>
            <h2>Nothing left to do</h2>
            <p>Every duplicate set has been handled.</p>
          </section>
        )}
      </div>

      <footer className="dup-safety">
        <IonIcon icon={refreshOutline} />
        <p>
          Nothing is erased. Removed copies go to your{' '}
          <strong>Recycle Bin</strong>, and you can put any of them back.
          {plan.crateIndexAvailable && (
            <>
              {' '}
              Serato crates are updated to point at the copy you keep —
              originals are backed up inside your <strong>_Serato_</strong>{' '}
              folder before anything is written.
            </>
          )}
        </p>
      </footer>
    </div>
  );
};

export default PlanOverview;
